// Pure logic for saved-view alerts: convert a stored view's filters into a
// scanner-log query, and diff each poll's rows down to the genuinely-fresh
// hits to alert on. Kept free of React/DOM/timers so it's unit-testable.

import type { ScannerLogEntry, SavedSearchFilters, ScannerLogFilters } from '@/lib/api-client';

/** How long to suppress re-alerting the SAME ticker within a view, so a hit
 *  that keeps re-qualifying every poll doesn't ping repeatedly. */
export const TICKER_COOLDOWN_MS = 10 * 60_000;

/** Convert a saved view's stored filters into a scanner-log query. Mirrors the
 *  scanner page's applySavedFilters mapping: minTotalEvents string→number,
 *  statuses→status CSV, and the legacy showStale/newOnly fallbacks.
 *
 *  NOTE: date (since/until) is intentionally NOT produced here — it's
 *  session-local and never part of SavedSearchFilters, so the alert watcher
 *  always polls the latest window regardless of any date filter the operator
 *  has set on-screen. (The watcher adds its own `since` watermark for paging.) */
export function savedFiltersToQuery(f: SavedSearchFilters): ScannerLogFilters {
  const q: ScannerLogFilters = {};
  if (f.severity?.length) q.severity = f.severity;
  if (f.reason?.length) {
    q.reason = f.reason;
    if (f.reasonMode) q.reasonMode = f.reasonMode;
  }
  if (f.category?.length) q.category = f.category;
  if (f.sessions?.length) q.sessions = f.sessions;
  if (f.passes) {
    q.passes = f.passes;
    // 'eq' makes passes match EXACTLY N (e.g. =1 → alert only on the first
    // qualifying pass, not every re-qualification). Default 'gte' (≥ N).
    if (f.passesMode === 'eq') q.passesMode = 'eq';
  }
  if (f.minTotalEvents) {
    const n = Number(f.minTotalEvents);
    if (Number.isFinite(n) && n > 0) q.minTotalEvents = n;
  }
  if (f.hasAutoBuy) q.hasAutoBuy = true;
  if (f.statuses?.length) {
    q.status = f.statuses.join(',');
  } else {
    // Legacy saved searches predating the statuses multi-select.
    if (f.newOnly) q.newOnly = true;
    if (f.showStale === false) q.excludeStale = true;
  }
  return q;
}

export interface AlertWatchState {
  /** Newest detectedAt (epoch ms) seen for this view; null = not yet baselined. */
  baselineMs: number | null;
  /** Row ids already alerted, so a row never double-fires. Bounded. */
  firedIds: Set<string>;
  /** ticker → last fired epoch ms, for the per-ticker cooldown. */
  tickerCooldown: Map<string, number>;
}

export function newAlertWatchState(): AlertWatchState {
  return { baselineMs: null, firedIds: new Set(), tickerCooldown: new Map() };
}

/** Pure: given a poll's rows and the running per-view state, return the fresh
 *  hits to alert on and MUTATE `state` forward. On the FIRST call for a view
 *  (no baseline) it only records the baseline and returns [] — so enabling an
 *  alert never dumps the existing backlog. Subsequent calls return rows newer
 *  than the baseline that haven't fired and aren't within the ticker cooldown.
 *  Returned rows are newest-first. */
export function computeFreshHits(
  rows: ScannerLogEntry[],
  state: AlertWatchState,
  nowMs: number,
  cooldownMs: number = TICKER_COOLDOWN_MS,
): ScannerLogEntry[] {
  const stamped = rows
    .map((r) => ({ r, ms: Date.parse(r.detectedAt) }))
    .filter((x) => Number.isFinite(x.ms));
  if (stamped.length === 0) return [];

  const newestMs = Math.max(...stamped.map((x) => x.ms));

  // First sighting → record baseline only, never alert the existing backlog.
  if (state.baselineMs == null) {
    state.baselineMs = newestMs;
    return [];
  }

  const baseline = state.baselineMs;
  const fresh: ScannerLogEntry[] = [];
  for (const { r, ms } of stamped) {
    if (ms <= baseline) continue; // not newer than what we've already accounted for
    if (state.firedIds.has(r.id)) continue; // already alerted this row
    const last = state.tickerCooldown.get(r.ticker);
    if (last != null && nowMs - last < cooldownMs) {
      state.firedIds.add(r.id); // mark seen so it can't fire after the cooldown lapses
      continue;
    }
    fresh.push(r);
    state.firedIds.add(r.id);
    state.tickerCooldown.set(r.ticker, nowMs);
  }

  state.baselineMs = Math.max(baseline, newestMs);

  // Bound memory growth on a long-lived session.
  if (state.firedIds.size > 500) {
    state.firedIds = new Set(Array.from(state.firedIds).slice(-250));
  }

  fresh.sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));
  return fresh;
}
